import {
  getAlternatePinCalloutColorOption,
  getPinAlternateNameEntries,
} from './pinAlternateNames';
import {
  getPinFilterMatchState,
  normalizePinFilterGroupList,
} from './pinFilterGroups';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const isDomElement = (value) => (
  typeof Element !== 'undefined'
  && value instanceof Element
);

const getPinFieldValue = (pin, fieldNames) => {
  for (const fieldName of fieldNames) {
    const value = pin?.[fieldName];
    if (value !== undefined && value !== null) {
      const normalized = String(value).trim();
      if (normalized) {
        return normalized;
      }
    }
  }

  return '';
};

const createSvgElement = (doc, tagName, attributes = {}) => {
  const element = doc.createElementNS(SVG_NAMESPACE, tagName);
  Object.entries(attributes).forEach(([name, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      element.setAttribute(name, String(value));
    }
  });
  return element;
};

const doesSvgElementMatchPin = (element, pin) => {
  if (!isDomElement(element) || !pin) {
    return false;
  }

  const elementSvgId = String(element.getAttribute('data-svgid') || '').trim();
  const pinSvgId = getPinFieldValue(pin, ['svg_id', 'svgId']);
  if (elementSvgId && pinSvgId && elementSvgId === pinSvgId) {
    return true;
  }

  const elementPinNumber = String(element.getAttribute('data-pin-number') || '').trim();
  const pinNumber = getPinFieldValue(pin, ['pin_number', 'pinNumber']);
  if (elementPinNumber && pinNumber && elementPinNumber === pinNumber) {
    return true;
  }

  const elementPinName = String(element.getAttribute('data-pin') || '').trim();
  const pinName = getPinFieldValue(pin, ['pin_name', 'pinName']);
  return Boolean(elementPinName && pinName && elementPinName === pinName);
};

const findCalloutGroupForPin = (svg, pin) => {
  const calloutGroups = Array.from(svg.querySelectorAll('g[data-micropinouts-callout="1"]'));

  for (const calloutGroup of calloutGroups) {
    const identityElement = calloutGroup.querySelector('[data-pin], [data-pin-number], [data-svgid]');
    if (!doesSvgElementMatchPin(identityElement, pin)) {
      continue;
    }

    return calloutGroup;
  }

  return null;
};

const getNumericSvgAttribute = (element, attributeName, fallback = NaN) => {
  if (!isDomElement(element)) {
    return fallback;
  }

  const parsed = Number.parseFloat(element.getAttribute(attributeName) || '');
  return Number.isFinite(parsed) ? parsed : fallback;
};

const transformSvgPoint = (matrix, x, y) => {
  if (!matrix) {
    return { x, y };
  }

  return {
    x: (matrix.a * x) + (matrix.c * y) + matrix.e,
    y: (matrix.b * x) + (matrix.d * y) + matrix.f,
  };
};

const getRectVisualBounds = (rect) => {
  if (!isDomElement(rect)) {
    return null;
  }

  const x = getNumericSvgAttribute(rect, 'x', 0);
  const y = getNumericSvgAttribute(rect, 'y', 0);
  const width = getNumericSvgAttribute(rect, 'width', 0);
  const height = getNumericSvgAttribute(rect, 'height', 0);
  const matrix = typeof rect.transform?.baseVal?.consolidate === 'function'
    ? rect.transform.baseVal.consolidate()?.matrix || null
    : null;
  const corners = [
    transformSvgPoint(matrix, x, y),
    transformSvgPoint(matrix, x + width, y),
    transformSvgPoint(matrix, x, y + height),
    transformSvgPoint(matrix, x + width, y + height),
  ];
  const xValues = corners.map((point) => point.x);
  const yValues = corners.map((point) => point.y);
  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
};

const getCircleCenter = (circle) => {
  if (!isDomElement(circle)) {
    return null;
  }

  const x = getNumericSvgAttribute(circle, 'cx', NaN);
  const y = getNumericSvgAttribute(circle, 'cy', NaN);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }

  return { x, y };
};

const parseSvgViewBox = (svg) => {
  if (!isDomElement(svg)) {
    return null;
  }

  const rawViewBox = String(svg.getAttribute('viewBox') || '').trim();
  if (rawViewBox) {
    const parts = rawViewBox
      .split(/[\s,]+/)
      .map((value) => Number.parseFloat(value))
      .filter((value) => Number.isFinite(value));

    if (parts.length === 4) {
      const [minX, minY, width, height] = parts;
      return {
        minX,
        minY,
        width,
        height,
        maxX: minX + width,
        maxY: minY + height,
      };
    }
  }

  const width = getNumericSvgAttribute(svg, 'width', NaN);
  const height = getNumericSvgAttribute(svg, 'height', NaN);
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return null;
  }

  return {
    minX: 0,
    minY: 0,
    width,
    height,
    maxX: width,
    maxY: height,
  };
};

const clamp = (value, min, max) => {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max) || min > max) {
    return value;
  }

  return Math.min(Math.max(value, min), max);
};

const getOptionalNumericValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (value === null || value === undefined) {
    return null;
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeRightAngleRotation = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  const normalizedValue = ((Math.round(numericValue / 90) * 90) % 360 + 360) % 360;
  return normalizedValue;
};

const getElementRotationDegrees = (element) => {
  if (!isDomElement(element)) {
    return 0;
  }

  const transform = String(element.getAttribute('transform') || '');
  const rotationMatch = transform.match(/rotate\(\s*(-?\d+(?:\.\d+)?)/i);

  if (!rotationMatch) {
    return 0;
  }

  return normalizeRightAngleRotation(rotationMatch[1]);
};

const measureSvgTextWidth = (svg, lines, fontSize, fontFamily) => {
  if (!isDomElement(svg) || !Array.isArray(lines) || lines.length === 0) {
    return 0;
  }

  const doc = svg.ownerDocument;
  const measurementText = createSvgElement(doc, 'text', {
    x: -99999,
    y: -99999,
    opacity: '0',
    'pointer-events': 'none',
    'font-size': fontSize,
    'font-family': fontFamily,
  });

  svg.appendChild(measurementText);

  let maxWidth = 0;

  lines.forEach((line) => {
    measurementText.textContent = line;

    let measuredWidth = 0;
    if (typeof measurementText.getComputedTextLength === 'function') {
      try {
        measuredWidth = measurementText.getComputedTextLength();
      } catch (error) {
        measuredWidth = 0;
      }
    }

    if (!Number.isFinite(measuredWidth) || measuredWidth <= 0) {
      measuredWidth = String(line || '').length * fontSize * 0.58;
    }

    maxWidth = Math.max(maxWidth, measuredWidth);
  });

  measurementText.remove();
  return Number(maxWidth.toFixed(2));
};

const getSvgPresentationValue = (element, computedStyle, cssPropertyName, attributeName, fallback = '') => {
  if (!isDomElement(element)) {
    return fallback;
  }

  const computedValue = (
    computedStyle?.getPropertyValue?.(cssPropertyName)
    || computedStyle?.[cssPropertyName]
    || ''
  ).trim();

  if (computedValue) {
    return computedValue;
  }

  const attributeValue = String(element.getAttribute(attributeName) || '').trim();
  return attributeValue || fallback;
};

const buildAlternatePinNameLines = (alternateNames) => {
  if (!Array.isArray(alternateNames) || alternateNames.length === 0) {
    return [];
  }

  if (alternateNames.length === 1) {
    return [alternateNames[0]];
  }

  if (alternateNames.length === 2) {
    const joined = alternateNames.join(' / ');
    return joined.length <= 28 ? [joined] : alternateNames;
  }

  const firstLine = alternateNames[0];
  const secondLineBase = alternateNames.slice(1, 3).join(' / ');
  const remainingCount = Math.max(0, alternateNames.length - 3);

  return [
    firstLine,
    remainingCount > 0 ? `${secondLineBase} +${remainingCount}` : secondLineBase,
  ].filter(Boolean);
};

export const removeAlternatePinNameMarkup = (svg) => {
  if (!isDomElement(svg)) {
    return;
  }

  svg
    .querySelectorAll(`
      [data-micropinouts-alt-pin-label="1"],
      [data-micropinouts-alt-pin-callout="1"],
      style[data-micropinouts-alt-pin-label-style="1"],
      style[data-micropinouts-alt-pin-callout-style="1"]
    `)
    .forEach((node) => node.remove());
};

export const renderAlternatePinNameMarkup = (svg, options = {}) => {
  if (!isDomElement(svg)) {
    return false;
  }

  removeAlternatePinNameMarkup(svg);

  const pins = Array.isArray(options.pins) ? options.pins : [];
  const showAlternatePinNames = options.showAlternatePinNames !== false;
  const showAlternatePinCalloutColors = Boolean(options.showAlternatePinCalloutColors);
  const normalizedActiveFilterGroups = normalizePinFilterGroupList(options.activeFilterGroups);
  const hasActiveFilterGroups = normalizedActiveFilterGroups.length > 0;
  const pinsWithAlternateNames = pins
    .map((pin) => ({
      pin,
      alternateNameEntries: getPinAlternateNameEntries(pin),
    }))
    .filter(({ alternateNameEntries }) => alternateNameEntries.length > 0);

  if (!showAlternatePinNames || pinsWithAlternateNames.length === 0) {
    return true;
  }

  const doc = svg.ownerDocument;
  const style = createSvgElement(doc, 'style', {
    'data-micropinouts-alt-pin-callout-style': '1',
  });
  style.textContent = `
    .micropinouts-alt-pin-callout-bubble {
      pointer-events: all;
      cursor: pointer;
    }
    .micropinouts-alt-pin-callout-text {
      dominant-baseline: middle;
      pointer-events: all;
      cursor: pointer;
    }
    .micropinouts-alt-pin-callout-text tspan {
      pointer-events: all;
    }
  `;
  svg.appendChild(style);

  pinsWithAlternateNames.forEach(({ pin, alternateNameEntries }) => {
    const calloutGroup = findCalloutGroupForPin(svg, pin);
    if (!isDomElement(calloutGroup)) {
      return;
    }

    const calloutBubble = calloutGroup.querySelector('rect.pin-callout-bubble[data-micropinouts-callout="1"]');
    const calloutText = calloutGroup.querySelector('text.pin-callout-text');
    if (!isDomElement(calloutBubble) || !isDomElement(calloutText)) {
      return;
    }

    const calloutAnchor = calloutGroup.querySelector('circle.pin-callout-anchor[data-micropinouts-callout="1"]');
    const bubbleBounds = getRectVisualBounds(calloutBubble);
    if (!bubbleBounds) {
      return;
    }

    const anchorCenter = getCircleCenter(calloutAnchor) || {
      x: bubbleBounds.centerX,
      y: bubbleBounds.centerY,
    };
    if (!Array.isArray(alternateNameEntries) || alternateNameEntries.length === 0) {
      return;
    }

    const defaultView = svg.ownerDocument?.defaultView;
    const computedTextStyle = typeof defaultView?.getComputedStyle === 'function'
      ? defaultView.getComputedStyle(calloutText)
      : null;
    const computedBubbleStyle = typeof defaultView?.getComputedStyle === 'function'
      ? defaultView.getComputedStyle(calloutBubble)
      : null;
    const baseFontSize = Number.parseFloat(
      calloutText.getAttribute('font-size')
      || computedTextStyle?.fontSize
      || '12'
    );
    const resolvedBaseFontSize = Number.isFinite(baseFontSize) ? baseFontSize : 12;
    const baseBubbleWidth = getNumericSvgAttribute(calloutBubble, 'width', bubbleBounds.width);
    const baseBubbleHeight = getNumericSvgAttribute(calloutBubble, 'height', bubbleBounds.height);
    const baseBubbleRadiusX = getNumericSvgAttribute(calloutBubble, 'rx', NaN);
    const baseBubbleRadiusY = getNumericSvgAttribute(calloutBubble, 'ry', NaN);
    const boxWidth = Number((
      Number.isFinite(baseBubbleWidth) && baseBubbleWidth > 0
        ? baseBubbleWidth
        : 52
    ).toFixed(2));
    const boxHeight = Number((
      Number.isFinite(baseBubbleHeight) && baseBubbleHeight > 0
        ? baseBubbleHeight
        : 24
    ).toFixed(2));
    const boxRadiusX = Number(
      (Number.isFinite(baseBubbleRadiusX) ? baseBubbleRadiusX : Math.max(1, Math.min(10, boxHeight * 0.22))).toFixed(2)
    );
    const boxRadiusY = Number(
      (Number.isFinite(baseBubbleRadiusY) ? baseBubbleRadiusY : boxRadiusX).toFixed(2)
    );
    const textPaddingX = Number(Math.max(4, Math.min(10, boxWidth * 0.12)).toFixed(2));
    const textPaddingY = Number(Math.max(3, Math.min(8, boxHeight * 0.16)).toFixed(2));
    const connectorGap = Number(Math.max(8, resolvedBaseFontSize * 0.9).toFixed(2));
    const baseBubbleStrokeWidth = getNumericSvgAttribute(calloutBubble, 'stroke-width', 1.4);
    const alternateStrokeWidth = Number(
      (Number.isFinite(baseBubbleStrokeWidth) ? baseBubbleStrokeWidth : 1.4).toFixed(2)
    );
    const alternateBubbleFill = getSvgPresentationValue(
      calloutBubble,
      computedBubbleStyle,
      'fill',
      'fill',
      'rgba(255, 255, 255, 0.94)'
    );
    const alternateBubbleStroke = getSvgPresentationValue(
      calloutBubble,
      computedBubbleStyle,
      'stroke',
      'stroke',
      '#475569'
    );
    const alternateBubbleFillOpacity = getSvgPresentationValue(
      calloutBubble,
      computedBubbleStyle,
      'fill-opacity',
      'fill-opacity',
      ''
    );
    const alternateBubbleStrokeOpacity = getSvgPresentationValue(
      calloutBubble,
      computedBubbleStyle,
      'stroke-opacity',
      'stroke-opacity',
      ''
    );
    const alternateBubbleOpacity = getSvgPresentationValue(
      calloutBubble,
      computedBubbleStyle,
      'opacity',
      'opacity',
      ''
    );
    const alternateBubbleStrokeDasharray = getSvgPresentationValue(
      calloutBubble,
      computedBubbleStyle,
      'stroke-dasharray',
      'stroke-dasharray',
      ''
    );
    const alternateBubbleStrokeDashoffset = getSvgPresentationValue(
      calloutBubble,
      computedBubbleStyle,
      'stroke-dashoffset',
      'stroke-dashoffset',
      ''
    );
    const alternateBubbleStrokeLinejoin = getSvgPresentationValue(
      calloutBubble,
      computedBubbleStyle,
      'stroke-linejoin',
      'stroke-linejoin',
      ''
    );
    const calloutRotationDeg = getElementRotationDegrees(calloutBubble)
      || getElementRotationDegrees(calloutText);
    const alternateCalloutIdentityAttributes = {
      'data-pin': calloutBubble.getAttribute('data-pin') || getPinFieldValue(pin, ['pin_name', 'pinName']),
      'data-group': calloutBubble.getAttribute('data-group') || getPinFieldValue(pin, ['group_name', 'groupName']),
      'data-pin-id': calloutBubble.getAttribute('data-pin-id') || getPinFieldValue(pin, ['id', 'pin_id', 'pinId']),
      'data-pin-number': calloutBubble.getAttribute('data-pin-number') || getPinFieldValue(pin, ['pin_number', 'pinNumber']),
      'data-svgid': calloutBubble.getAttribute('data-svgid') || getPinFieldValue(pin, ['svg_id', 'svgId']),
      'data-group-color': calloutBubble.getAttribute('data-group-color') || alternateBubbleStroke,
    };
    const alternateFontFamily = (
      calloutText.getAttribute('font-family')
      || computedTextStyle?.fontFamily
      || 'Arial, sans-serif'
    );
    const alternateTextFill = getSvgPresentationValue(
      calloutText,
      computedTextStyle,
      'fill',
      'fill',
      '#0f172a'
    );
    const alternateTextFillOpacity = getSvgPresentationValue(
      calloutText,
      computedTextStyle,
      'fill-opacity',
      'fill-opacity',
      ''
    );
    const alternateTextOpacity = getSvgPresentationValue(
      calloutText,
      computedTextStyle,
      'opacity',
      'opacity',
      ''
    );
    const alternateTextFontWeight = getSvgPresentationValue(
      calloutText,
      computedTextStyle,
      'font-weight',
      'font-weight',
      ''
    );
    const alternateTextFontStyle = getSvgPresentationValue(
      calloutText,
      computedTextStyle,
      'font-style',
      'font-style',
      ''
    );
    const alternateTextLetterSpacing = getSvgPresentationValue(
      calloutText,
      computedTextStyle,
      'letter-spacing',
      'letter-spacing',
      ''
    );
    const alternateTextAnchor = getSvgPresentationValue(
      calloutText,
      computedTextStyle,
      'text-anchor',
      'text-anchor',
      'middle'
    );
    const availableTextWidth = Math.max(8, boxWidth - (textPaddingX * 2));
    const availableTextHeight = Math.max(8, boxHeight - (textPaddingY * 2));
    const baseLineHeightRatio = 1.18;
    const viewBoxBounds = parseSvgViewBox(svg);
    const outwardRight = bubbleBounds.centerX >= anchorCenter.x;
    const { isActive: isPinFilterGroupActive } = getPinFilterMatchState(
      pin,
      normalizedActiveFilterGroups
    );
    const desiredX = outwardRight
      ? bubbleBounds.maxX + connectorGap
      : bubbleBounds.minX - connectorGap - boxWidth;
    const alignedY = bubbleBounds.centerY - (boxHeight / 2);
    const outerMargin = 4;
    const firstManualX = getOptionalNumericValue(alternateNameEntries?.[0]?.x);
    const defaultBaseX = Number.isFinite(firstManualX)
      ? firstManualX
      : (
        viewBoxBounds
          ? clamp(desiredX, viewBoxBounds.minX + outerMargin, viewBoxBounds.maxX - boxWidth - outerMargin)
          : desiredX
      );
    const xStepDirection = defaultBaseX > 0 ? 1 : -1;

    alternateNameEntries.forEach((alternateNameEntry, index) => {
      const alternateLines = buildAlternatePinNameLines([alternateNameEntry.name]);
      if (alternateLines.length === 0) {
        return;
      }

      const configuredColor = getAlternatePinCalloutColorOption(alternateNameEntry?.color);
      const useConfiguredColor = showAlternatePinCalloutColors && isPinFilterGroupActive && configuredColor;
      const useMutedColor = showAlternatePinCalloutColors && hasActiveFilterGroups && !isPinFilterGroupActive;
      const alternateBubbleFillValue = useConfiguredColor
        ? configuredColor.fill
        : useMutedColor
          ? 'rgba(255, 255, 255, 0.92)'
          : alternateBubbleFill;
      const alternateBubbleStrokeValue = useConfiguredColor
        ? configuredColor.stroke
        : useMutedColor
          ? '#94a3b8'
          : alternateBubbleStroke;
      const alternateTextFillValue = useConfiguredColor
        ? configuredColor.text
        : useMutedColor
          ? '#94a3b8'
          : alternateTextFill;
      const alternateBubbleFillOpacityValue = useConfiguredColor || useMutedColor
        ? '1'
        : alternateBubbleFillOpacity;
      const alternateBubbleStrokeOpacityValue = useConfiguredColor || useMutedColor
        ? '1'
        : alternateBubbleStrokeOpacity;
      const alternateTextFillOpacityValue = useConfiguredColor || useMutedColor
        ? '1'
        : alternateTextFillOpacity;
      const alternateBubbleOpacityValue = useConfiguredColor || useMutedColor
        ? '1'
        : alternateBubbleOpacity;
      const alternateTextOpacityValue = useConfiguredColor || useMutedColor
        ? '1'
        : alternateTextOpacity;

      const measuredWidthAtBaseSize = measureSvgTextWidth(
        svg,
        alternateLines,
        resolvedBaseFontSize,
        alternateFontFamily
      );
      const baseTextBlockHeight = resolvedBaseFontSize * (
        1 + (Math.max(0, alternateLines.length - 1) * baseLineHeightRatio)
      );
      const widthScale = measuredWidthAtBaseSize > 0
        ? availableTextWidth / measuredWidthAtBaseSize
        : 1;
      const heightScale = baseTextBlockHeight > 0
        ? availableTextHeight / baseTextBlockHeight
        : 1;
      const alternateFontScale = Math.min(1, widthScale, heightScale);
      const alternateFontSize = Number(
        Math.max(4.2, resolvedBaseFontSize * alternateFontScale).toFixed(2)
      );
      const lineHeight = Number((alternateFontSize * baseLineHeightRatio).toFixed(2));
      const textBlockHeight = Number((
        alternateFontSize
        + (Math.max(0, alternateLines.length - 1) * lineHeight)
      ).toFixed(2));
      const defaultY = alignedY;
      const manualX = getOptionalNumericValue(alternateNameEntry?.x);
      const manualY = getOptionalNumericValue(alternateNameEntry?.y);
      const hasManualX = Number.isFinite(manualX);
      const hasManualY = Number.isFinite(manualY);
      const unresolvedX = hasManualX
        ? manualX
        : defaultBaseX + (index * boxWidth * xStepDirection);
      const unresolvedY = hasManualY
        ? manualY
        : defaultY;
      const alternateX = unresolvedX;
      const alternateY = !hasManualY && viewBoxBounds
        ? clamp(unresolvedY, viewBoxBounds.minY + outerMargin, viewBoxBounds.maxY - boxHeight - outerMargin)
        : unresolvedY;

      const alternateGroup = createSvgElement(doc, 'g', {
        class: 'micropinouts-alt-pin-callout',
        'data-micropinouts-alt-pin-callout': '1',
        'aria-hidden': 'true',
        transform: calloutRotationDeg
          ? `rotate(${calloutRotationDeg} ${Number((alternateX + (boxWidth / 2)).toFixed(2))} ${Number((alternateY + (boxHeight / 2)).toFixed(2))})`
          : undefined,
        ...alternateCalloutIdentityAttributes,
      });

      const alternateBubble = createSvgElement(doc, 'rect', {
        class: 'micropinouts-alt-pin-callout-bubble',
        x: Number(alternateX.toFixed(2)),
        y: Number(alternateY.toFixed(2)),
        width: boxWidth,
        height: boxHeight,
        fill: alternateBubbleFillValue,
        'fill-opacity': alternateBubbleFillOpacityValue,
        rx: boxRadiusX,
        ry: boxRadiusY,
        stroke: alternateBubbleStrokeValue,
        'stroke-opacity': alternateBubbleStrokeOpacityValue,
        'stroke-width': alternateStrokeWidth,
        'stroke-dasharray': alternateBubbleStrokeDasharray,
        'stroke-dashoffset': alternateBubbleStrokeDashoffset,
        'stroke-linejoin': alternateBubbleStrokeLinejoin,
        opacity: alternateBubbleOpacityValue,
        ...alternateCalloutIdentityAttributes,
      });
      alternateGroup.appendChild(alternateBubble);

      const alternateText = createSvgElement(doc, 'text', {
        class: 'micropinouts-alt-pin-callout-text',
        fill: alternateTextFillValue,
        'fill-opacity': alternateTextFillOpacityValue,
        'font-size': alternateFontSize,
        'font-family': alternateFontFamily,
        'font-weight': alternateTextFontWeight,
        'font-style': alternateTextFontStyle,
        'letter-spacing': alternateTextLetterSpacing,
        opacity: alternateTextOpacityValue,
        'text-anchor': alternateTextAnchor,
        ...alternateCalloutIdentityAttributes,
      });
      const firstLineY = Number((
        alternateY
        + ((boxHeight - textBlockHeight) / 2)
        + (alternateFontSize / 2)
      ).toFixed(2));

      alternateLines.forEach((line, lineIndex) => {
        const tspan = createSvgElement(doc, 'tspan', {
          x: Number((alternateX + (boxWidth / 2)).toFixed(2)),
          y: Number((firstLineY + (lineIndex * lineHeight)).toFixed(2)),
        });
        tspan.textContent = line;
        alternateText.appendChild(tspan);
      });

      alternateGroup.appendChild(alternateText);
      calloutGroup.appendChild(alternateGroup);
    });
  });

  return true;
};
